/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Document key (DK) generation + content encryption with AAD binding.
 * Implements stages 2-3 of the create-message flow per the redesign doc.
 *
 *   Cipher: XChaCha20-Poly1305 (F4 wrapper)
 *   Key:    32 random bytes from the F3 RNG seam, no reuse across documents
 *   Nonce:  24 random bytes from the F3 RNG seam, fresh per encrypt
 *   AAD:    typed-field byte concatenation of (documentId, version,
 *           algorithmId), reconstructed at decrypt-time from metadata
 *           the recipient already has — no extra wire state needed.
 *
 * Without AAD, a ciphertext can be silently moved to a different
 * documentId at the API layer; with AAD bound to documentId+version,
 * any such substitution fails the AEAD tag check at decrypt.
 *
 * AAD layout mirrors `src/services/vault/aad.ts` (typed-field byte
 * concatenation, magic bytes, length-prefixed strings, trailing domain
 * byte). The two AADs use different magic + domain bytes so they can
 * never collide even by accident.
 *
 * See `docs/Application_redesign.md` §3 stages 2-3 and
 * `docs/plan-crypto-redesign.md` §F13.
 */

import {
  encrypt as aeadEncrypt,
  decrypt as aeadDecrypt,
} from '../crypto/aead.js';
import { defaultRandom, type Random } from '../random.js';

/** Cipher identity locked at the AAD layer. */
export const ALGORITHM_ID = 'XChaCha20-Poly1305' as const;

/** AAD layout version — bumps require a new parser; old never gets re-read. */
export const DOCUMENT_AAD_VERSION = 1;

/** 32-byte XChaCha20-Poly1305 key. */
export const DK_LENGTH_BYTES = 32;

/** 24-byte XChaCha20-Poly1305 nonce. */
export const DOCUMENT_NONCE_LENGTH_BYTES = 24;

const MAGIC_BYTES = new Uint8Array([0x42, 0x44, 0x44, 0x31]); // "BDD1"
const DOMAIN_DOC = 0x01;

/**
 * Thrown when document decryption fails — covers AEAD tag mismatch
 * (tampered ciphertext, wrong DK, wrong nonce, mismatched AAD inputs)
 * and length-validation failures from the underlying primitive.
 */
export class DocumentDecryptError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'DocumentDecryptError';
  }
}

/**
 * Generate a fresh 32-byte document key. No reuse across documents:
 * each new document MUST call this once and use the returned bytes
 * exactly once.
 *
 * @param rng - randomness source (defaults to `defaultRandom`)
 */
export const generateDocumentKey = (rng: Random = defaultRandom): Uint8Array =>
  rng.getRandomBytes(DK_LENGTH_BYTES);

const u8 = (n: number): Uint8Array => Uint8Array.of(n & 0xff);

const u16be = (n: number): Uint8Array =>
  Uint8Array.of((n >>> 8) & 0xff, n & 0xff);

const concat = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
};

const lenPrefix8 = (b: Uint8Array): Uint8Array => concat(u8(b.length), b);
const lenPrefix16 = (b: Uint8Array): Uint8Array => concat(u16be(b.length), b);

/**
 * Build the deterministic AAD bytes for a document context.
 *
 * Layout:
 *   "BDD1"                            (4 bytes magic)
 *   u16be(DOCUMENT_AAD_VERSION = 1)   (2 bytes)
 *   u16be(version)                    (2 bytes — caller-supplied document version)
 *   lenPrefix16(utf8(documentId))     (2 + N)
 *   lenPrefix8(utf8(ALGORITHM_ID))    (1 + 18 = 19)
 *   u8(DOMAIN_DOC = 0x01)             (1 byte)
 *
 * Recipient reconstructs the same bytes from `(documentId, version)`
 * adjacent to the ciphertext — no AAD bytes need to travel on the wire.
 *
 * @throws if documentId UTF-8 exceeds 65535 bytes (the lenPrefix16
 *   ceiling) or `version` is negative / out of u16 range.
 */
export const buildDocumentAad = (params: {
  documentId: string;
  version: number;
}): Uint8Array => {
  const { documentId, version } = params;
  if (!Number.isInteger(version) || version < 0 || version > 0xffff) {
    throw new Error(
      `document version must be a u16 integer (got ${String(version)})`
    );
  }
  if (documentId.length === 0) {
    throw new Error('document id must be non-empty');
  }
  const idBytes = new TextEncoder().encode(documentId);
  if (idBytes.length > 0xffff) {
    throw new Error('document id UTF-8 must be ≤ 65535 bytes');
  }
  const algoBytes = new TextEncoder().encode(ALGORITHM_ID);
  // ALGORITHM_ID is fixed at 18 bytes ('XChaCha20-Poly1305'); the
  // length-prefix bound check is here to make the contract explicit
  // for future algorithm rotation.
  if (algoBytes.length > 0xff) {
    throw new Error('algorithmId UTF-8 must be ≤ 255 bytes');
  }
  return concat(
    MAGIC_BYTES,
    u16be(DOCUMENT_AAD_VERSION),
    u16be(version),
    lenPrefix16(idBytes),
    lenPrefix8(algoBytes),
    u8(DOMAIN_DOC)
  );
};

/** Returned by `encryptDocumentContent`. */
export interface EncryptedDocument {
  /** Ciphertext including the 16-byte Poly1305 tag suffix. */
  ciphertext: Uint8Array;
  /** 24-byte nonce used for this encryption. */
  nonce: Uint8Array;
  /** The deterministic AAD bytes used (returned for round-trip use). */
  aad: Uint8Array;
}

/**
 * Encrypt document content under a fresh nonce, binding the ciphertext
 * to its `(documentId, version, ALGORITHM_ID)` context via AAD.
 *
 * @param params.content     - plaintext bytes (any length, may be empty)
 * @param params.dk          - 32-byte document key from `generateDocumentKey`
 * @param params.documentId  - non-empty document id (typically a UUID)
 * @param params.version     - data-version u16 (1 for new documents)
 * @param params.rng         - randomness source for the nonce
 *                             (defaults to `defaultRandom`)
 *
 * @returns ciphertext + nonce + reconstructed AAD bytes
 * @throws {InvalidKeySizeError} if `dk.length !== 32`
 * @throws if AAD inputs violate length boundaries (see `buildDocumentAad`)
 */
export const encryptDocumentContent = (params: {
  content: Uint8Array;
  dk: Uint8Array;
  documentId: string;
  version: number;
  rng?: Random;
}): EncryptedDocument => {
  const { content, dk, documentId, version, rng = defaultRandom } = params;
  const aad = buildDocumentAad({ documentId, version });
  const nonce = rng.getRandomBytes(DOCUMENT_NONCE_LENGTH_BYTES);
  const ciphertext = aeadEncrypt({
    key: dk,
    nonce,
    plaintext: content,
    aad,
  });
  return { ciphertext, nonce, aad };
};

/**
 * Decrypt document ciphertext. The caller supplies the AAD bytes (or
 * reconstructs them from `(documentId, version)` via `buildDocumentAad`);
 * any divergence from what was bound at encrypt time fails the AEAD tag.
 *
 * @throws {DocumentDecryptError} if the tag does not verify (covers
 *   tampered ciphertext, wrong DK, wrong nonce, mismatched AAD inputs).
 */
export const decryptDocumentContent = (params: {
  ciphertext: Uint8Array;
  nonce: Uint8Array;
  dk: Uint8Array;
  aad: Uint8Array;
}): Uint8Array => {
  const { ciphertext, nonce, dk, aad } = params;
  try {
    return aeadDecrypt({ key: dk, nonce, ciphertext, aad });
  } catch (err) {
    throw new DocumentDecryptError(
      `document decrypt failed: ${err instanceof Error ? err.message : String(err)}`,
      { cause: err }
    );
  }
};
